import { useState } from "react";

/**
 * Agent examples — how people actually use Axon.
 *
 * Each example is a real prompt you'd say to your Claude/Cursor/Hermes
 * agent, paired with the MCP tool chain Axon dispatches and a one-line
 * outcome. Tabbed by user persona so a visitor can self-identify
 * (trader · DeFi user · automator · marketplace buyer).
 *
 * Goal: someone scrolls here and immediately sees "oh, this is for me."
 */

type Persona = "trader" | "defi" | "automator" | "buyer";

interface Example {
  persona: Persona;
  prompt: string;
  tools: string[];
  outcome: string;
}

const PERSONAS: Array<{ id: Persona; label: string; tag: string; blurb: string }> = [
  {
    id: "trader",
    label: "Active trader",
    tag: "perps",
    blurb: "You want your Claude/Cursor agent to read the book, open/manage perps, and respect a kill switch.",
  },
  {
    id: "defi",
    label: "DeFi user",
    tag: "spot · lend",
    blurb: "You want to rebalance, swap, supply, and watch your Aave health factor — without leaving your agent.",
  },
  {
    id: "automator",
    label: "Strategy runner",
    tag: "cron · signal",
    blurb: "You want a strategy on a schedule. Signed, sandboxed, and inside your local RiskGuard.",
  },
  {
    id: "buyer",
    label: "Marketplace buyer",
    tag: "x402 · signed",
    blurb: "You want to buy a strategy from a published author, pay in USDC, and have it run under your policy.",
  },
];

const EXAMPLES: Record<Persona, Example[]> = {
  trader: [
    {
      persona: "trader",
      prompt: "Open a 3x BTC long on Hyperliquid with a 2% stop and 5% take-profit. Use 5% of available margin.",
      tools: ["get_balance", "get_ticker", "open_perp", "audit_verify"],
      outcome: "Validated against policy (max 10x · daily-loss cap intact) → signed by trade-only key → filled. SL/TP armed in the daemon's sweep.",
    },
    {
      persona: "trader",
      prompt: "What did I miss while I was AFK? Any positions in trouble?",
      tools: ["get_pending_alerts", "get_positions"],
      outcome: "Agent reports: BTC long auto-closed on SL, ETH long still healthy, kill-switch never tripped. One-click ack.",
    },
    {
      persona: "trader",
      prompt: "Cancel every open order and close all perps. Now.",
      tools: ["kill_switch_on", "close_perp", "cancel_order"],
      outcome: "Kill switch armed locally → every open order cancelled, perps closed per policy, strategies paused. Audited in the same stream.",
    },
  ],
  defi: [
    {
      persona: "defi",
      prompt: "Swap 0.5 WETH to USDC on Uniswap on Base. Max 30 bps slippage.",
      tools: ["get_ticker", "open_perp"],
      outcome: "Quoter returns expected out, slippage check passes policy (≤50 bps), swap signed locally and submitted. Settlement audited.",
    },
    {
      persona: "defi",
      prompt: "Supply 2,000 USDC to Aave on Arbitrum. Show me the APY first.",
      tools: ["get_ticker", "open_perp", "get_positions"],
      outcome: "Agent reports current supply APY → you approve → supply tx signed → aToken position appears in your dashboard.",
    },
    {
      persona: "defi",
      prompt: "My Aave health factor just dropped below 1.5 — what should I do?",
      tools: ["get_pending_alerts", "get_positions", "open_perp"],
      outcome: "Alert surfaced before agent answers. Suggested action: repay 50% of USDC debt. One click → HF 1.18 → 1.62.",
    },
  ],
  automator: [
    {
      persona: "automator",
      prompt: "Run the prism-momentum strategy on BTC every 4 hours.",
      tools: ["list_strategies", "schedule_strategy"],
      outcome: "Cron registered locally · signed strategy runs every 4h · each run goes through RiskGuard like a manual intent · failures emit strategy_errored alerts.",
    },
    {
      persona: "automator",
      prompt: "Pull today's Prism signal and trade it if conviction is above 70.",
      tools: ["get_prism_signal", "open_perp", "audit_verify"],
      outcome: "Signal fetched → conviction 78 → 2x ETH long opened with policy-bounded size. If conviction had been below 70, agent would have skipped.",
    },
  ],
  buyer: [
    {
      persona: "buyer",
      prompt: "Show me the top 5 signed strategies for BTC on Hyperliquid.",
      tools: ["marketplace_search", "list_strategies"],
      outcome: "Strykr-signed manifests only · ed25519 verified at build-time-pinned pubkey · sorted by install count + recent P&L.",
    },
    {
      persona: "buyer",
      prompt: "Buy 'BTC Mean Reversion v3' and run it on my account.",
      tools: ["install_strategy", "run_strategy"],
      outcome: "x402 USDC payment (EIP-3009) → manifest signature verified → strategy installed → first run dispatched. Your RiskGuard is the auth boundary, not the marketplace.",
    },
  ],
};

export function AgentExamples() {
  const [persona, setPersona] = useState<Persona>("trader");
  const examples = EXAMPLES[persona];
  const meta = PERSONAS.find((p) => p.id === persona)!;

  return (
    <section id="examples" className="ax-section">
      <div className="ax-section-head">
        <div className="ax-eyebrow">02 · What your agent can do</div>
        <h2 className="ax-h2">
          Talk to your agent.<br />
          <span style={{ color: "var(--at-fg-dim)" }}>Axon handles the rest.</span>
        </h2>
        <p className="ax-lede" style={{ marginTop: 18, maxWidth: 720 }}>
          Once <code style={inlineCode}>axon skills install</code> wires the skill into your runtime, your agent in Claude Code, Cursor, Codex, Hermes — any of 8 — has the full tool surface. Below: real prompts, the tools Axon dispatches, the outcome.
        </p>
      </div>

      <div className="ex-personas" role="tablist" aria-label="User persona">
        {PERSONAS.map((p) => (
          <button
            key={p.id}
            role="tab"
            aria-selected={persona === p.id}
            onClick={() => setPersona(p.id)}
            className={"ex-persona" + (persona === p.id ? " active" : "")}
          >
            <span className="ex-persona-label">{p.label}</span>
            <span className="ex-persona-tag">{p.tag}</span>
          </button>
        ))}
      </div>

      <div className="ex-blurb">{meta.blurb}</div>

      <div className="ex-list">
        {examples.map((ex, i) => (
          <div key={i} className="ex-card">
            <div className="ex-prompt">
              <span className="ex-prompt-tag">YOU</span>
              <span className="ex-prompt-text">"{ex.prompt}"</span>
            </div>
            <div className="ex-arrow" aria-hidden>↓</div>
            <div className="ex-tools">
              <span className="ex-tools-tag">AXON CALLS</span>
              <div className="ex-tools-chain">
                {ex.tools.map((t) => (
                  <span key={t} className="ex-tool">{t}</span>
                ))}
              </div>
            </div>
            <div className="ex-arrow" aria-hidden>↓</div>
            <div className="ex-outcome">
              <span className="ex-outcome-tag">OUTCOME</span>
              <span className="ex-outcome-text">{ex.outcome}</span>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .ex-personas {
          display: flex; flex-wrap: wrap; gap: 6px;
          margin-bottom: 12px;
        }
        .ex-persona {
          display: inline-flex; align-items: center; gap: 8px;
          font-family: inherit;
          padding: 9px 14px;
          background: transparent;
          border: 1px solid var(--at-hairline);
          border-radius: var(--at-radius-full);
          color: var(--at-fg-dim);
          cursor: pointer;
          transition: border-color var(--at-dur-fast) var(--at-ease),
                      color var(--at-dur-fast) var(--at-ease),
                      background var(--at-dur-fast) var(--at-ease);
        }
        .ex-persona:hover { border-color: var(--at-hairline-strong); color: var(--at-fg); }
        .ex-persona.active {
          background: rgba(74, 222, 128, 0.08);
          border-color: rgba(74, 222, 128, 0.4);
          color: var(--at-accent);
        }
        .ex-persona-label { font-size: 13.5px; font-weight: 500; }
        .ex-persona-tag {
          font-family: var(--at-font-mono);
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--at-muted);
        }
        .ex-persona.active .ex-persona-tag { color: var(--at-accent); }

        .ex-blurb {
          font-size: 14.5px;
          color: var(--at-fg-dim);
          line-height: 1.6;
          margin-bottom: 28px;
          max-width: 720px;
          padding: 14px 18px;
          background: var(--at-surface);
          border: 1px solid var(--at-hairline);
          border-radius: 10px;
        }

        .ex-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
          gap: 16px;
        }
        .ex-card {
          background: var(--at-surface);
          border: 1px solid var(--at-hairline);
          border-radius: 12px;
          padding: 20px 22px;
          display: flex; flex-direction: column;
          gap: 10px;
        }
        .ex-card:hover { border-color: rgba(74,222,128,0.25); }

        .ex-prompt {
          display: flex; flex-direction: column; gap: 6px;
          padding: 12px 14px;
          background: var(--at-bg);
          border: 1px solid var(--at-hairline);
          border-radius: 8px;
        }
        .ex-prompt-tag {
          font-family: var(--at-font-mono);
          font-size: 9.5px;
          letter-spacing: 0.24em;
          color: var(--at-muted);
        }
        .ex-prompt-text {
          font-size: 13.5px;
          line-height: 1.5;
          color: var(--at-fg);
          font-style: italic;
        }

        .ex-arrow {
          text-align: center;
          color: var(--at-muted);
          font-size: 12px;
          line-height: 1;
        }

        .ex-tools {
          display: flex; flex-direction: column; gap: 8px;
          padding: 10px 14px;
          background: rgba(74,222,128,0.04);
          border: 1px solid rgba(74,222,128,0.18);
          border-radius: 8px;
        }
        .ex-tools-tag {
          font-family: var(--at-font-mono);
          font-size: 9.5px;
          letter-spacing: 0.24em;
          color: var(--at-accent);
        }
        .ex-tools-chain { display: flex; flex-wrap: wrap; gap: 5px; }
        .ex-tool {
          font-family: var(--at-font-mono);
          font-size: 10.5px;
          padding: 3px 8px;
          background: var(--at-bg);
          border: 1px solid var(--at-hairline);
          border-radius: 4px;
          color: var(--at-fg-dim);
        }

        .ex-outcome {
          display: flex; flex-direction: column; gap: 6px;
          padding: 12px 14px;
          background: var(--at-bg);
          border: 1px solid var(--at-hairline);
          border-radius: 8px;
        }
        .ex-outcome-tag {
          font-family: var(--at-font-mono);
          font-size: 9.5px;
          letter-spacing: 0.24em;
          color: var(--at-muted);
        }
        .ex-outcome-text {
          font-size: 13px;
          line-height: 1.55;
          color: var(--at-fg-dim);
        }

        @media (max-width: 720px) {
          .ex-list { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
}

const inlineCode: React.CSSProperties = {
  background: "var(--at-bg)",
  padding: "1px 6px",
  borderRadius: 4,
  fontFamily: "var(--at-font-mono)",
  fontSize: 13,
  color: "var(--at-accent)",
};
